<!-- MainFollowModal.vue -->
<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import FollowingForm from "./FollowingForm.vue";
import FollowersForm from "./FollowersForm.vue";

const props = defineProps({
  initialView: {
    type: String,
    default: "summary",
  },
});

const emit = defineEmits(["close"]);

// 'summary' (Seguidores) | 'personal' (Seguindo)
const activeView = ref(
  props.initialView === "personal" ? "personal" : "summary"
);

// Se o pai mudar a view inicial
watch(
  () => props.initialView,
  (val) => {
    if (val === "summary" || val === "personal") {
      activeView.value = val;
    }
  }
);

function closeModal() {
  emit("close");
}

function handleEscKey(e) {
  if (e.key === "Escape") closeModal();
}

onMounted(() => {
  document.addEventListener("keydown", handleEscKey);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscKey);
});
</script>

<template>
  <Teleport to="body">
    <div class="modal-overlay">
      <div class="modal-panel">
        <!-- Cabeçalho -->
        <header class="modal-header">
          <h3 class="card-title">Seguidores / Seguindo</h3>

          <button
            @click="closeModal"
            type="button"
            class="modal-close-btn"
            aria-label="Fechar"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              ></path>
            </svg>
          </button>
        </header>

        <div class="panel-body">
          <div class="content-area">
            <!-- Tabs -->
            <div class="tab-buttons-wrapper">
              <div class="tab-buttons">
                <button
                  type="button"
                  class="tab-button"
                  :class="{ active: activeView === 'personal' }"
                  @click="activeView = 'personal'"
                >
                  <i class="fas fa-user-friends mr-1.5"></i>
                  Seguindo
                </button>

                <button
                  type="button"
                  class="tab-button"
                  :class="{ active: activeView === 'summary' }"
                  @click="activeView = 'summary'"
                >
                  <i class="fas fa-users mr-1.5"></i>
                  Seguidores
                </button>
              </div>
            </div>

            <!-- Conteúdo -->
            <section class="tab-content">
              <FollowersForm
                v-if="activeView === 'summary'"
                :isVisible="activeView === 'summary'"
              />

              <FollowingForm
                v-else
                :isVisible="activeView === 'personal'"
              />
            </section>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" type="button" class="btn btn-secondary">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* ============================================================
   BOTÕES DE ABA
============================================================ */

.tab-button {
  border: none;
  background: transparent;
  padding: 0.4rem 0.9rem;
  margin-right: 0.5rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.tab-button:hover:not(:disabled) {
  background-color: var(--brand-tint);
}

.tab-button:active:not(:disabled) {
  transform: translateY(1px);
}

.tab-button:focus,
.tab-button:focus-visible {
  outline: none;
  box-shadow: none;
}

.tab-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tab-button:disabled:hover {
  cursor: not-allowed;
}

.tab-button.active {
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  border: none;
}

.tab-button.active:hover:not(:disabled) {
  background-color: var(--brand-soft-hover);
}

.modal-panel {
  max-width: 490px;
  height: min(92vh, 860px);
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

/* ============================================================
   BODY
============================================================ */

.panel-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.content-area {
  flex: 1;
  min-height: 0;
  padding: 0.75rem 1rem 20px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow: hidden;
}

.tab-buttons-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
}

.tab-buttons {
  display: inline-flex;
  gap: 0.25rem;
}

.tab-content {
  flex: 1;
  min-height: 0;
  margin-top: 0.25rem;
  overflow-y: auto;
}

</style>